/**
 * Default placement of the backbone sites on the topology canvas.
 *
 * Dagre lays the sites out left-to-right by link rank, which scatters the
 * backbone in an order nobody recognises. The operations team reads it as
 * a rough map instead — Dallas up top, the Querétaro sites in the middle
 * row, the Mexico City sites at the bottom, the Televisa premises on the
 * right — so the known sites get fixed anchors (site centres, canvas
 * units). Any site without one keeps its dagre position, moved below the
 * anchored block so it can never land on top of it.
 *
 * Keyed by the topology site id (`TopologyNode.site`), not the datacenter.
 */
export const SITE_ANCHORS: Readonly<Record<string, { x: number; y: number }>> = {
    USDAL01: { x: 0, y: 0 },
    MXNLE01: { x: 260, y: 290 },
    MXQRO02: { x: 520, y: 640 },
    MXQRO01: { x: 1320, y: 640 },
    TUCHP: { x: 1980, y: 640 },
    MXMEX02: { x: 520, y: 1700 },
    MXMEX05: { x: 1360, y: 1700 },
    TUSNA: { x: 1990, y: 1700 },
    MXMEX06: { x: 920, y: 2060 },
};

/** Vertical gap between the anchored block and any unanchored site. */
const UNANCHORED_GAP = 600;

/**
 * Centre of every site: its anchor when it has one, otherwise its dagre
 * centre shifted below the lowest anchored site.
 */
export function resolveSiteCenters(
    siteIds: string[],
    dagreCenter: (siteId: string) => { x: number; y: number }
): Record<string, { x: number; y: number }> {
    const anchoredYs = siteIds
        .filter((id) => SITE_ANCHORS[id])
        .map((id) => SITE_ANCHORS[id].y);
    const offsetY =
        anchoredYs.length > 0 ? Math.max(...anchoredYs) + UNANCHORED_GAP : 0;

    const centers: Record<string, { x: number; y: number }> = {};
    for (const id of siteIds) {
        const anchor = SITE_ANCHORS[id];
        if (anchor) {
            centers[id] = anchor;
            continue;
        }
        const fallback = dagreCenter(id);
        centers[id] = { x: fallback.x, y: fallback.y + offsetY };
    }
    return centers;
}
